// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
} from '@/constants/hardfin-standard-icons';
import { MembershipReportData } from '@/membership/api/membership-types';
import { ChartAxis } from '@/membership/components/chart-axis';
import { ChartCursorTooltip } from '@/membership/components/chart-cursor-tooltip';
import { ChartDateAxis } from '@/membership/components/chart-date-axis';
import { ChartHoveredDay } from '@/membership/components/chart-hovered-day';
import { MembershipDeltaChart } from '@/membership/components/membership-delta-chart';
import { MembershipViewState } from '@/membership/hooks/use-membership-view';
import {
  AXIS_WIDTH,
  PlotLayout,
  toAxisScale,
  toDayAtOffset,
  toDayBand,
  toPlotLayout,
} from '@/membership/utilities/chart-geometry';
import { SkuSeries, toDayDate } from '@/membership/utilities/membership-days';
import {
  Box,
  Chip,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import {
  MouseEvent,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// The chart stands beside the list rather than under it, so it takes a real
// height rather than the strip a sparkline would need.
const DEFAULT_CHART_HEIGHT = 380;

// The delta chart reads a handful of units either side of zero rather than the
// whole fleet, so it needs a fraction of the height to say the same amount.
const DEFAULT_DELTA_HEIGHT = 120;

// How many gaps the count axis aims for. The step is rounded up to a value
// worth reading, so the labels come out near this rather than at it.
const AXIS_TARGET_INTERVALS = 5;

// The bars are plain elements with inline styles rather than MUI ones. A year
// of them is thousands of nodes, and putting an sx prop on each cost more in
// Emotion serialising styles than the whole render did.
const BAR_OPACITY = 0.75;

// Half of the widest date the scrub bar writes, such as "Sep 30, 2026". The
// label is centred on the thumb, so this is how far from either edge it has to
// stop to stay inside the chart.
const HALF_DATE_LABEL = 44;

interface Props {
  report: MembershipReportData;
  view: MembershipViewState;
  // Set where the report sits inside something smaller than the tab.
  chartHeight?: number;
  deltaChartHeight?: number;
  // How the serials are reached, and where they land. A dialog opens them
  // beside itself from a button, and the tab expands them in place under a
  // header, so the caller owns both rather than the view branching on a mode.
  serialsControl?: ReactNode;
  serialsBelow?: ReactNode;
}

/**
 * A project's membership over its window: the count chart, the scrub bar under
 * it, the rollforward, the daily change chart, and a chip per SKU across the
 * top.
 *
 * A chip filters both charts, the rollforward and the serials at once, so every
 * part of the panel answers the same question.
 */
export function MembersView(props: Props) {
  const view = props.view;
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);

  // The bar arithmetic needs a pixel width, which CSS cannot hand over. The
  // measured box carries no padding of its own, because contentRect excludes
  // padding and a value derived from it would shrink the next measurement and
  // oscillate.
  useEffect(() => {
    const element = frameRef.current;
    if (element === null) return undefined;
    const observer = new ResizeObserver((entries) => {
      setFrameWidth(entries[0].contentRect.width);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // One layout for both charts and everything under them, so a day sits in the
  // same place all the way down the panel.
  const layout = useMemo(
    () =>
      toPlotLayout(frameWidth, view.visibleDays, window.devicePixelRatio || 1),
    [frameWidth, view.visibleDays]
  );

  // The day under the cursor lives here rather than in either chart, so
  // hovering one lights the same day up in the other.
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const windowEndOn = view.windowStartOn.plus({ days: view.visibleDays - 1 });

  const setDayIndex = view.setDayIndex;
  const windowStart = view.windowStart;
  const selectDay = useCallback(
    (day: number) => {
      setDayIndex(windowStart + day);
    },
    [setDayIndex, windowStart]
  );

  return (
    <Stack gap={2} ref={frameRef}>
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Filter
        </Typography>
        {view.totals.map((total) => {
          const isHidden = view.isHidden(total.itemSku);
          return (
            <Tooltip
              arrow
              key={total.itemSku}
              title={isHidden ? 'Show this SKU' : 'Hide this SKU'}
            >
              <Chip
                size="small"
                variant={isHidden ? 'outlined' : 'filled'}
                label={total.itemSku}
                onClick={() => {
                  view.toggleSku(total.itemSku);
                }}
                icon={
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      ml: 1,
                      backgroundColor: view.colors[total.itemSku],
                      opacity: isHidden ? 0.35 : 1,
                    }}
                  />
                }
                sx={{ opacity: isHidden ? 0.6 : 1 }}
              />
            </Tooltip>
          );
        })}

        <Box sx={{ flexGrow: 1 }} />

        <IconButton
          size="small"
          aria-label="Earlier window"
          disabled={!view.canPageBack}
          onClick={view.pageBack}
        >
          <ChevronLeftRounded fontSize="small" />
        </IconButton>
        <FilterToggleGroup
          value={view.zoom}
          options={view.zoomOptions}
          onChange={view.setZoom}
          ariaLabel="Days the chart shows"
        />
        <IconButton
          size="small"
          aria-label="Later window"
          disabled={!view.canPageForward}
          onClick={view.pageForward}
        >
          <ChevronRightRounded fontSize="small" />
        </IconButton>
      </Stack>

      <PlotSpan layout={layout}>
        <Typography variant="subtitle2">Total members</Typography>
      </PlotSpan>

      <CountChart
        series={view.series}
        colors={view.colors}
        height={props.chartHeight ?? DEFAULT_CHART_HEIGHT}
        startOn={view.windowStartOn}
        endOn={windowEndOn}
        onSelectDay={selectDay}
        hoveredDay={hoveredDay}
        onHoverDay={setHoveredDay}
        layout={layout}
      />

      <PlotSpan layout={layout}>
        <Typography variant="subtitle2">Daily membership change</Typography>
      </PlotSpan>

      <MembershipDeltaChart
        report={props.report}
        deltas={view.deltas}
        itemSkus={view.shownSkus}
        windowStart={view.windowStart}
        colors={view.colors}
        height={props.deltaChartHeight ?? DEFAULT_DELTA_HEIGHT}
        startOn={view.windowStartOn}
        endOn={windowEndOn}
        onSelectDay={selectDay}
        hoveredDay={hoveredDay}
        onHoverDay={setHoveredDay}
        layout={layout}
      />

      <PlotSpan layout={layout}>
        {/* Inset by half a bar each side. The thumb runs from the centre of the
            first bar to the centre of the last, where an uninset slider put day
            one at the plot's left edge and half a bar from its own column. */}
        <Box sx={{ mx: `${String(layout.barWidth / 2)}px` }}>
          <Slider
            size="small"
            min={view.windowStart}
            max={view.windowStart + view.visibleDays - 1}
            value={view.dayIndex}
            disabled={view.visibleDays <= 1}
            onChange={(_event, next) => {
              view.setDayIndex(next as number);
            }}
            aria-label="Day of the project"
            sx={{
              py: 0.5,
              // The bar reads as one range rather than a filled portion,
              // because the thumb is the reading and everything either side of
              // it is the same window.
              '& .MuiSlider-track': { border: 'none', opacity: 0.38 },
              '& .MuiSlider-rail': { opacity: 0.38 },
            }}
          />

          {/* Anchored to the thumb, so the day the rest of the panel answers
              is readable without dragging. It shares the slider's inset, and
              its ends are clamped to keep half a label from hanging outside
              the chart. */}
          <Box sx={{ position: 'relative', height: 18 }}>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                left: `clamp(${String(HALF_DATE_LABEL)}px, ${String(
                  view.visibleDays <= 1
                    ? 50
                    : ((view.dayIndex - view.windowStart) /
                        (view.visibleDays - 1)) *
                        100
                )}%, calc(100% - ${String(HALF_DATE_LABEL)}px))`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {view.dayDate.toLocaleString(DateTime.DATE_MED)}
            </Typography>
          </Box>
        </Box>

        {props.serialsControl !== undefined && (
          <Box sx={{ mt: 3 }}>{props.serialsControl}</Box>
        )}
      </PlotSpan>

      {props.serialsBelow !== undefined && (
        <PlotSpan layout={layout}>
          <Box sx={{ mt: 1 }}>{props.serialsBelow}</Box>
        </PlotSpan>
      )}
    </Stack>
  );
}

// Everything that is not a chart is held to the same span as the plots, so a
// day lines up with its bar.
function PlotSpan(props: { layout: PlotLayout; children: ReactNode }) {
  return (
    <Box
      sx={{
        marginLeft: `${String(AXIS_WIDTH)}px`,
        width: `${String(props.layout.plotWidth)}px`,
      }}
    >
      {props.children}
    </Box>
  );
}

interface CountChartProps {
  series: SkuSeries[];
  colors: Record<string, string>;
  height: number;
  startOn: DateTime;
  endOn: DateTime;
  onSelectDay: (dayIndex: number) => void;
  // The hovered day is shared with the chart below, so the two light up
  // together.
  hoveredDay: number | null;
  onHoverDay: (dayIndex: number | null) => void;
  layout: PlotLayout;
}

// Which day and which stacked segment the cursor is over, in viewport
// coordinates so the tooltip can be placed without measuring anything else.
interface CountHover {
  x: number;
  y: number;
  day: number;
  itemSku: string | null;
}

/**
 * How many members were on site on each day, stacked by SKU, over a count axis.
 * Clicking a day scrubs to it.
 */
function CountChart(props: CountChartProps) {
  const [hover, setHover] = useState<CountHover | null>(null);

  const dayCount = props.series.at(0)?.counts.length ?? 0;
  // Recomputed only when the data changes, because both of these sit in the
  // path of every mouse move across the plot.
  const totals = useMemo(
    () =>
      Array.from({ length: dayCount }, (_unused, day) =>
        props.series.reduce((total, one) => total + one.counts[day], 0)
      ),
    [props.series, dayCount]
  );
  // Every bar is a share of the top of the axis, so the tallest reaches the
  // round number above it rather than the ceiling.
  const scale = useMemo(
    () => toAxisScale(Math.max(0, ...totals), AXIS_TARGET_INTERVALS),
    [totals]
  );
  const labels = [...scale.ticks].reverse().map((tick) => String(tick));

  const toDay = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return toDayAtOffset(
      event.clientX - bounds.left,
      bounds.width,
      props.layout,
      dayCount
    );
  };

  return (
    <>
      <Stack>
        <Stack direction="row" sx={{ height: props.height }}>
          <ChartAxis labels={labels} />

          <Box
            data-testid="membership-chart"
            sx={{
              width: `${String(props.layout.plotWidth)}px`,
              flexShrink: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              borderBottom: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              position: 'relative',
            }}
            onMouseMove={(event) => {
              const day = toDay(event);
              if (day === null) return;
              props.onHoverDay(day);
              setHover({
                x: event.clientX,
                y: event.clientY,
                day,
                itemSku: toHoveredSku(
                  props.series,
                  day,
                  scale.max,
                  event.currentTarget.getBoundingClientRect(),
                  event.clientY
                ),
              });
            }}
            onMouseLeave={() => {
              props.onHoverDay(null);
              setHover(null);
            }}
            onClick={(event) => {
              const day = toDay(event);
              if (day !== null) props.onSelectDay(day);
            }}
          >
            <ChartHoveredDay
              day={props.hoveredDay}
              layout={props.layout}
              dayCount={dayCount}
            />

            {/* Before the bars, so a line reads behind them. Zero has the
              plot's own bottom rule, so every label above it carries its
              own. */}
            {scale.ticks.slice(1).map((tick) => (
              <Box
                key={tick}
                aria-hidden
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${String((1 - tick / scale.max) * 100)}%`,
                  borderTop: 1,
                  borderColor: 'divider',
                  pointerEvents: 'none',
                }}
              />
            ))}

            <CountBars
              series={props.series}
              colors={props.colors}
              axisMax={scale.max}
              layout={props.layout}
            />
          </Box>

          <ChartAxis labels={labels} isRight />
        </Stack>

        <ChartDateAxis
          layout={props.layout}
          startOn={props.startOn}
          endOn={props.endOn}
        />
      </Stack>

      {hover !== null && (
        <CountTooltip
          hover={hover}
          series={props.series}
          colors={props.colors}
          total={totals[hover.day]}
          startOn={props.startOn}
        />
      )}
    </>
  );
}

/**
 * Which stacked segment the cursor sits in. Segments are drawn top down and the
 * stack is bottom aligned, so the walk runs from the last series up.
 */
function toHoveredSku(
  series: SkuSeries[],
  day: number,
  axisMax: number,
  bounds: DOMRect,
  clientY: number
): string | null {
  const fromBottom = (bounds.bottom - clientY) / bounds.height;
  let stacked = 0;
  for (let i = series.length - 1; i >= 0; i -= 1) {
    stacked += series[i].counts[day] / axisMax;
    if (fromBottom <= stacked) {
      return series[i].counts[day] > 0 ? series[i].itemSku : null;
    }
  }
  return null;
}

interface CountTooltipProps {
  hover: CountHover;
  series: SkuSeries[];
  colors: Record<string, string>;
  total: number;
  startOn: DateTime;
}

function CountTooltip(props: CountTooltipProps) {
  const shown = props.series.filter((one) => one.counts[props.hover.day] > 0);

  return (
    <ChartCursorTooltip
      x={props.hover.x}
      y={props.hover.y}
      testId="membership-chart-tooltip"
    >
      <Typography
        variant="subtitle2"
        sx={{ pb: 0.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {toDayDate(props.startOn, props.hover.day).toLocaleString(
          DateTime.DATE_MED
        )}
      </Typography>
      {/* The count leads, because it is what the day amounts to. It carries a
          tint rather than bold, so it reads as the total without competing
          with the date above it. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          mt: 0.5,
          // Bled out to the tooltip's own padding, so the tint reads as a band
          // across the row rather than as a box around the text.
          mx: -1,
          px: 1,
          py: 0.25,
          backgroundColor: 'action.selected',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Members
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {props.total}
        </Typography>
      </Stack>

      <Stack gap={0.25} sx={{ mt: 0.5 }}>
        {shown.map((one) => (
          <Stack
            key={one.itemSku}
            direction="row"
            alignItems="center"
            gap={0.75}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: props.colors[one.itemSku],
              }}
            />
            <Typography
              variant="caption"
              sx={{
                flexGrow: 1,
                fontWeight: one.itemSku === props.hover.itemSku ? 600 : 400,
              }}
            >
              {one.itemSku}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: one.itemSku === props.hover.itemSku ? 600 : 400,
              }}
            >
              {one.counts[props.hover.day]}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </ChartCursorTooltip>
  );
}

interface CountBarsProps {
  series: SkuSeries[];
  colors: Record<string, string>;
  axisMax: number;
  layout: PlotLayout;
}

/**
 * The stacked bars, one column per day.
 *
 * A year of them is thousands of elements, and rebuilding those on every mouse
 * move made a dense chart unusable. Nothing here reads the hovered day, so the
 * whole grid is skipped while the cursor moves and only the tint above it and
 * the tooltip are redrawn.
 */
const CountBars = memo(function CountBars(props: CountBarsProps) {
  const dayCount = props.series.at(0)?.counts.length ?? 0;

  return (
    <>
      {Array.from({ length: dayCount }, (_unused, day) => (
        <div
          key={day}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            width: `${String(toDayBand(day, props.layout, dayCount).width)}px`,
            marginRight: `${String(
              day === dayCount - 1 ? 0 : props.layout.gapWidth
            )}px`,
            flexShrink: 0,
            height: '100%',
            // Positioned, so the hovered day's tint stays behind the bars.
            position: 'relative',
          }}
        >
          {props.series.map((one) => (
            <div
              key={one.itemSku}
              style={{
                height: `${String((one.counts[day] / props.axisMax) * 100)}%`,
                backgroundColor: props.colors[one.itemSku],
                opacity: BAR_OPACITY,
              }}
            />
          ))}
        </div>
      ))}
    </>
  );
});
